<template>
    <article>
        <p>
            Most methodologies for analyzing the performance of any system lie on two basic metrics:
            success rate and latency of requests.
            I wonder, why this simple and straightforward approach isn't applied in database management?
            Every monitoring integration for Postgres I've seen collects tons of metrics, but neither the success rate nor the latency of queries is among them.
            It's probably because it's hard to do properly. In this post, I'll explain how we implemented gathering said metrics in our open-source exporters.
        </p>

        <h2>Postgres query statistics</h2>

        <p>
            There are few data sources in Postgres where you can get query statistics.
        </p>

        <ul>
            <li>
                <var>pg_stat_statements</var> &mdash; stores statistics about successfully completed queries. If a query is failed or canceled, its statistics
                will not be taken into account.
                <v-img max-width="80%" class="ma-auto my-6" src="/static/img/blog/pg_stat_statements_visibility.svg" />
            </li>
            <li>
                <var>pg_stat_activity</var> &mdash; shows the currently running queries
                <v-img max-width="80%" class="ma-auto my-6" src="/static/img/blog/pg_stat_activity_visibility.svg" />
            </li>
            <li>
                Log &mdash; contains all the errors that occurred and statistics of the queries that executed longer than <var>log_min_duration_statement</var>
                <v-img max-width="80%" class="ma-auto my-6" src="/static/img/blog/pg_log_visibility.svg" />
            </li>
        </ul>

        <p>
            As seen in the diagrams above, we can't get a full picture based on just one of these sources.
        </p>

        <p>
            <a href="https://github.com/coroot/coroot-pg-agent" target="_blank">Coroot-pg-agent</a>
            combines statistics from <var>pg_stat_activity</var> and <var>pg_stat_statements</var>,
            so the resulting metrics reflect the actual latency of both completed queries and those still in progress.
            Implementation of such a combination is challenging for several reasons:
        </p>

        <ul>
            <li>
                We need a key to join statistics related to a particular query from these two views.
                <var>pg_stat_activity</var> doesn't contain <var>query_id</var> in versions prior to 14.0.
                We aim to support Postgres starting from version 9.6, so the agent uses normalized query text as the key.
            </li>
            <li>
                The specific query is present either in <var>pg_stat_activity</var> or in <var>pg_stat_statements</var>
                at a specific point in time.

                However, we cannot simply sum the counters taken from these views,
                because the resulting counter will decrease if the query fails or is canceled.
                To avoid this, the agent returns the previous value of a counter if its value has decreased.
                <v-img max-width="80%" class="ma-auto my-6" src="/static/img/blog/pg_stat_merging.svg" />
            </li>

            <li>

                <var>pg_stat_statements.total_exec_time</var> is a wall-clock timer, it doesn't take into account that
                a query could be executed in parallel.
                <var>pg_stat_activity</var>, on the contrary, contains multiple rows for a query that uses multiple workers.
                As a result, the agent calculates the execution time based only on the connections with <var>backend_type = 'client backend'</var>.
            </li>
        </ul>

        <h2>Quantiles calculation</h2>

        <p>
            Having combined the statistics from <var>pg_stat_statements</var> and <var>pg_stat_activity</var>, now it's time to calculate a summary
            of Postgres latency.
            For a particular type of query, we can only calculate the average execution time as <var>total_execution_time / calls</var>.
            However, each Postgres server usually performs many different queries, so we can use the average execution time of each query to calculate
            overall latency quantiles. Let's see how it works using the example below.
        </p>

        <p>
            A server performed four types of queries. We know the number of queries of each type and the total execution time,
            so we can calculate the average query time for each group:
        </p>

        <v-simple-table dense>
            <thead>
                <tr><th>Query</th><th>Calls</th><th>Total time</th><th>Average time</th></tr>
            </thead>
            <tbody>
                <tr><td>query-1</td><td>100</td><td>500 ms</td><td>5 ms</td></tr>
                <tr><td>query-2</td><td>150</td><td>300 ms</td><td>2 ms</td></tr>
                <tr><td>query-3</td><td>200</td><td>2000 ms</td><td>10 ms</td></tr>
                <tr><td>query-4</td><td>500</td><td>500 ms</td><td>1 ms</td></tr>
            </tbody>
        </v-simple-table>


        <v-img max-width="80%" class="ma-auto my-6" src="/static/img/blog/pg_overall_latency.svg" />

        <p>
            In this case, the server served <var>100+150+200+500=950</var> queries.
            We can assume, that the latency of each individual query from a group is the average latency of this group.
            As a result, we get:
        </p>

        <ul>
            <li>0.5 quantile is the latency of the 475<sup>th</sup> element: 1 ms</li>
            <li>0.75 quantile is the latency of the 712<sup>th</sup> element: 5 ms</li>
            <li>0.95 quantile is the latency of the 902<sup>nd</sup> element: 10 ms</li>
        </ul>

        <p>
            This rather simple approach works more accurately in a real environment where the number of query groups is much
            higher than in my example.
        </p>

        <h2>Errors</h2>

        <p>
            Unfortunately, Postgres does not report in any system view the number of errors that occurred.
            Therefore, the only way to count errors is to parse the log.
        </p>

        <p>
            Our Prometheus exporters <a href="https://github.com/coroot/coroot-node-agent" target="_blank">node-agent</a> and <a href="https://github.com/coroot/coroot-aws-agent" target="_blank">aws-agent</a> parse logs and expose
            the log-based metrics <MetricLink name="container_log_messages_total" exporter="node-agent"/> and
            <MetricLink name="aws_rds_log_messages_total" exporter="aws-agent"/>.

            Since these metrics are broken down by message severity and pattern, we can easily calculate the number of errors in the Postgres log
            regardless of whether the instance is RDS or self-managed.
        </p>

        <pre v-hl.sh>
# HELP aws_rds_log_messages_total Number of messages grouped by the automatically extracted repeated pattern
# TYPE aws_rds_log_messages_total counter
aws_rds_log_messages_total{rds_instance_id: "us-east-2/pg-main-1", level: "critical", sample: "FATAL: connection to client lost",  pattern_hash: "4174a0b78f3a79718ab011317c9f413b"} 11
aws_rds_log_messages_total{rds_instance_id: "us-east-2/pg-main-1", level: "error", sample: "ERROR: canceling statement due to user request", pattern_hash: "13f3416616c667c6d36d9a4bcf742008"} 22
        </pre>

        <h2>Service Level Objectives (SLOs) for databases</h2>

        <p>
            Now we have metrics that are the basic SLIs (Service Level Indicators) of a Postgres server.
            Let's try to define SLOs (Service Level Objectives) &mdash; the target levels of each indicator.
        </p>

        <p>
            Choosing a universal threshold for database latency is a complicated task
            since application requirements can significantly vary from one app to another.
            For example, some apps need to execute queries in less than a millisecond, while for others 100ms is more than enough.
        </p>

        <p>
            At Coroot, we realized that the SLOs of any database depends on the SLOs of its consumers.
            For each database client not meeting its SLOs, we check the correlation between their affected SLIs and the SLIs of the database.
            The existence of a strong correlation between them means that this database has an issue affecting an app.
        </p>

        <p>
            On the one hand, this approach detects even the slightest performance degradations that
            are affecting the database clients. On the other hand, the probability of false positives is slim since such
            checks are not fired if all database clients meet their SLOs.
        </p>


        <p>
            The example below shows how the SLIs of the client application correlate with the SLI of the Postgres server:
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg-missing-metrics/correlation.png" />
        </v-card>


        <h2>Conclusion</h2>

        <p>
            SRE practices, such as SLIs & SLOs, are quite applicable to databases.
            The success rate and latency of queries are great basic SLIs of any database server.
            Our open-source Prometheus exporters can help you in collecting these and many other useful metrics.
        </p>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Get started with Coroot</router-link>
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-pg-agent" target="_blank">pg-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-node-agent" target="_blank">node-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-aws-agent" target="_blank">aws-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
        </ul>

    </article>
</template>

<script>
import MetricLink from "@/components/MetricLink";

export default {
    components: {MetricLink}
}
</script>

<style scoped>
</style>
